import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Paper,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { PowerBIEmbed } from "powerbi-client-react";
import { models } from "powerbi-client";

import { app_title } from "../config";
import userDetailApi from "../hooks/GET/userDetail";
import categoriesApi from "../hooks/GET/categories";
import visualizationApi from "../hooks/GET/visualization";
import reportAuthTokenApi from "../hooks/GET/reportAuthToken";
import {
  startSessionActivity,
  stopSessionActivity,
} from "../lib/sessionTracking";

function Dashboard() {
  // userId
  const [userId, isAdmin, isDefault] = useSelector((state) => [state.user.id, state.user.isAdmin, state.user.isDefault]);


  // branding details
  const brandingDtl = useSelector((state) => state.branding);

  // fetching user detail for category permission check start
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (userId) {
      userDetailApi(userId)
        .then((data) => {

          const permissionsId = data.permissions.map((e) => e.id);
          setPermissions(permissionsId);
        })
        .catch((error) => console.log(error));
    }
  }, [userId]);
  // fetching user detail for category permission check end

  // category options logic start
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    categoriesApi()
      .then((data) => {

        setCategoryOptions(data);
      })
      .catch((error) => console.log(error));
  }, []);
  // category options logic end

  // report auth token logic start
  const [reportAuthToken, setReportAuthToken] = useState("");

  useEffect(() => {
    reportAuthTokenApi()
      .then((data) => {
        setReportAuthToken(data);
      })
      .catch((error) => console.log(error));
  }, []);
  // report auth token logic end

  // visualization options logic start
  const [vizualizationOptions, setVizualizationOptions] = useState({});

  useEffect(() => {
    visualizationApi()
      .then((data) => {
        const dataObject = {};
        data.forEach((obj) => {
          obj.categories.forEach((cobj) => {
            if (!dataObject[cobj.id]) {
              // if category id key pair not created create it with empty array
              dataObject[cobj.id] = [];
            }
            dataObject[cobj.id].push({
              id: obj.id,
              name: obj.name,
              description: obj.description,
              embedCode: obj.embedCode,
              groupId: obj.groupId,
              reportId: obj.reportId,
              isProtected: obj.isProtected,
            });
          });
        });
        setVizualizationOptions(dataObject);
      })
      .catch((error) => console.log(error));
  }, []);
  // visualization options logic end

  // state logic start
  const [state, setState] = useState({
    category: "",
    visualization: "",
    reportZoomLevel: 0,
  });
  const previousActivityKeyRef = useRef("");
  const selectedCategory = state.category ? JSON.parse(state.category) : null;
  const selectedVisualization = state.visualization
    ? JSON.parse(state.visualization)
    : null;
  const activeCategoryId = selectedCategory?.id || null;
  const activeVisualizationId = selectedVisualization?.id || null;
  const activeActivityKey =
    activeCategoryId && activeVisualizationId
      ? `${activeCategoryId}:${activeVisualizationId}`
    : "";

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setState({ ...state, category: value, visualization: "" });
  };

  const handleVisualizationChange = (e) => {
    const value = e.target.value;
    setState({ ...state, visualization: value, reportZoomLevel: 0 });
  };

  const handleReportZoomLevelChange = (e) => {
    const value = e.target.value ? e.target.valueAsNumber : 0;
    setState({ ...state, reportZoomLevel: value });
  };
  // state logic end

  useEffect(() => {
    if (isDefault) {
      previousActivityKeyRef.current = "";
      return;
    }

    const syncSessionActivity = async () => {
      if (
        previousActivityKeyRef.current &&
        previousActivityKeyRef.current !== activeActivityKey
      ) {
        await stopSessionActivity().catch(() => {});
      }

      if (
        activeCategoryId &&
        activeVisualizationId &&
        previousActivityKeyRef.current !== activeActivityKey
      ) {
        await startSessionActivity({
          categoryId: activeCategoryId,
          visualizationId: activeVisualizationId,
        }).catch(() => {});
      }

      previousActivityKeyRef.current = activeActivityKey;
    };

    syncSessionActivity();
  }, [activeActivityKey, activeCategoryId, activeVisualizationId, isDefault]);

  useEffect(() => {
    return () => {
      if (previousActivityKeyRef.current && !isDefault) {
        stopSessionActivity().catch(() => {});
      }
    };
  }, [isDefault]);

  return (
    <Box sx={{ overflow: "auto", paddingX: 5, paddingY: 4, minHeight: "82vh" }}>
      <Helmet>
        <title>Dashboard | {brandingDtl.pageTitle || app_title}</title>
      </Helmet>
      <Grid container spacing={2} sx={{ minWidth: 300 }}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            label="Category"
            name="category"
            value={state.category}
            onChange={handleCategoryChange}
          >
            {categoryOptions.map(
              (option, idx) =>
                ((isAdmin && isDefault) || (permissions.length
                  ? permissions.includes(option.id)
                  : false)) && (
                  <MenuItem key={idx} value={JSON.stringify(option)}>
                    {option.name}
                  </MenuItem>
                )
            )}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            label="Visualization"
            name="visualization"
            value={state.visualization}
            onChange={handleVisualizationChange}
          >
            {(state.category
              ? vizualizationOptions[JSON.parse(state.category).id] || []
              : []
            ).map((option, idx) => (
              <MenuItem key={idx} value={JSON.stringify(option)}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {state.visualization && (


          <Grid item xs={12} md={4}>
            {selectedVisualization.isProtected && (
              <Grid container justifyContent={{ md: "flex-end", xs: 'flex-start' }}>
                <TextField
                  label="Zoom Level"
                  name="reportZoomLevel"
                  value={state.reportZoomLevel}
                  onChange={handleReportZoomLevelChange}
                  type="number"
                  inputProps={{ min: 0, max: 5, step: 0.1, }}
                  sx={{ minWidth: 100 }}
                />
              </Grid>
            )}
          </Grid>




        )}
        {state.visualization && (
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Paper variant="outlined" sx={{ mt: 2, border: 'none' }}>
              {selectedVisualization.isProtected ? (
                <PowerBIEmbed
                  embedConfig={{
                    type: "report", // Supported types: report, dashboard, tile, visual and qna
                    id: selectedVisualization.reportId, // ReportId
                    embedUrl: selectedVisualization.embedCode,
                    accessToken: reportAuthToken,
                    tokenType: models.TokenType.Aad,
                    settings: {
                      panes: { filters: { expanded: false, visible: false } },

                      background: models.BackgroundType.Default,
                      zoomLevel: state.reportZoomLevel,
                      layoutType: models.LayoutType.Custom,
                      customLayout: {
                        displayOption: models.DisplayOption.FitToPage
                      },
                      bars: {
                        statusBar: {
                          visible: true
                        }
                      }
                    },
                  }}
                  cssClassName={"protected-report"}
                  getEmbeddedComponent={(embeddedReport) => {

                    window.report = embeddedReport;

                  }}
                />
              ) : (
                <iframe
                  width="100%"
                  style={{ height: "80vh" }}
                  frameBorder="0"
                  loading="lazy"
                  src={selectedVisualization.embedCode}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  referrerPolicy="no-referrer"
                  title={app_title}
                />
              )}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Dashboard;
